/**
 * The `referenceData` payload the description tools (Description Regen and the
 * bulk rewrite) send to the `enrichReference` callable.
 *
 * Lives outside `services/firebase.ts` so it can be tested without initialising
 * Firebase; `firebase.ts` re-exports it, so existing imports are unchanged.
 */
import type { WatchReference } from '../types';
import { buildEnrichReferencePayload } from './enrichReferencePayload';
import type { SiblingReference } from './notableVariantCandidates';

/**
 * Keys the description tools leave out on top of what no caller ever sends.
 *
 * - `description` — omitted so the AI always writes a fresh, clean description
 *   from the specs rather than being anchored to the raw sales text it is
 *   replacing.
 * - `originalDescription` — the pre-AI-rewrite snapshot of that same text;
 *   sending it beside the omission would hand the model exactly what it is
 *   meant not to see.
 * - `occasion` — left out of description generation deliberately.
 */
const DESCRIPTION_OMIT = ['description', 'originalDescription', 'occasion'] as const;

/**
 * Build the `referenceData` payload for the enrichReference Cloud Function from a
 * stored ref doc — only primitive (string/number) spec fields plus the brand
 * name and real catalogue siblings. Objects and arrays (movement, etc.) are
 * skipped: the description is written from the flat spec fields.
 */
export function buildDescriptionEnrichSnapshot(
  ref: WatchReference,
  brandName: string,
  siblingReferences: SiblingReference[],
): Record<string, unknown> {
  return buildEnrichReferencePayload(ref as unknown as Record<string, unknown>, {
    brandName,
    siblingReferences,
    omit: DESCRIPTION_OMIT,
    primitivesOnly: true,
  });
}
